import type { CSSProperties } from "react";

/**
 * Portato da `components/money/Amount.jsx` del design system.
 *
 * Differenza rispetto all'originale: accetta **centesimi interi**, non un
 * decimale. Un `number` con la virgola è esattamente ciò che questa app non
 * può permettersi, e lasciare la porta aperta invita a passarci un float.
 */
const TONES = {
  neutral: "var(--text-primary)",
  positive: "var(--money-positive)",
  negative: "var(--money-negative)",
  settled: "var(--money-settled)",
  inverse: "var(--white)",
} as const;

export type AmountTone = "auto" | keyof typeof TONES;

export interface AmountProps {
  /** Importo in centesimi interi. */
  cents: number;
  currency?: string;
  size?: number;
  tone?: AmountTone;
  showSign?: boolean;
  style?: CSSProperties;
}

export function Amount({
  cents,
  currency = "€",
  size = 28,
  tone = "auto",
  showSign = false,
  style,
}: AmountProps) {
  const absolute = Math.abs(cents);
  const integerPart = Math.trunc(absolute / 100).toLocaleString("it-IT");
  const decimals = String(absolute % 100).padStart(2, "0");

  const color =
    tone === "auto"
      ? cents < 0
        ? TONES.negative
        : cents > 0
          ? TONES.positive
          : TONES.settled
      : TONES[tone];

  return (
    <span
      className="qota-num"
      style={{
        fontFamily: "var(--font-numeric)",
        fontWeight: 600,
        fontSize: size,
        lineHeight: 1.05,
        letterSpacing: "-0.02em",
        color,
        display: "inline-flex",
        alignItems: "baseline",
        ...style,
      }}
    >
      {(showSign || cents < 0) && <span>{cents < 0 ? "−" : "+"}</span>}
      <span>{integerPart}</span>
      <span style={{ fontSize: "0.62em", opacity: 0.62, marginLeft: "0.06em" }}>,{decimals}</span>
      <span style={{ fontSize: "0.62em", marginLeft: "0.18em", opacity: 0.62 }}>{currency}</span>
    </span>
  );
}
